import z, { ZodType } from 'zod';
import getEnv, { getEnvOrFail } from './getEnv';

type SchemaToType<T extends Record<string, ZodType>> = {
  [K in keyof T]: z.infer<T[K]>;
};

type EnvGetter<TEnv> = {
  env: TEnv;
  getEnv: <K extends keyof TEnv>(
    property: K,
    defaultValue?: TEnv[K],
  ) => TEnv[K];
  getEnvOrFail: <K extends keyof TEnv>(property: K) => NonNullable<TEnv[K]>;
};

function createEnv<
  TServer extends Record<string, ZodType>,
  TClient extends Record<string, ZodType>,
>({
  server,
  client,
  runtimeEnv,
}: {
  server: TServer;
  client: TClient;
  runtimeEnv: Record<string, string | undefined>;
}): EnvGetter<SchemaToType<TServer & TClient>> {
  // Validate server environment variables
  const serverEnv = Object.entries(server).reduce((acc, [key, schema]) => {
    try {
      acc[key as keyof TServer] = schema.parse(
        process.env[key],
      ) as SchemaToType<TServer>[keyof TServer];
    } catch (error) {
      console.error(
        JSON.stringify({
          message: `Invalid server environment variable ${key}`,
          error,
        }),
      );
    }
    return acc;
  }, {} as SchemaToType<TServer>);

  // Validate client environment variables
  const clientEnv = Object.entries(client).reduce((acc, [key, schema]) => {
    try {
      acc[key as keyof TClient] = schema.parse(
        runtimeEnv[key],
      ) as SchemaToType<TClient>[keyof TClient];
    } catch (error) {
      console.error(
        JSON.stringify({
          message: `Invalid client environment variable ${key}`,
          error,
        }),
      );
    }
    return acc;
  }, {} as SchemaToType<TClient>);

  // Combine server and client environment variables
  const combinedEnv = { ...serverEnv, ...clientEnv } as SchemaToType<
    TServer & TClient
  >;

  // Create wrapped versions of getEnv and getEnvOrFail that use our validated environment
  const getValidatedEnv = <K extends keyof typeof combinedEnv>(
    property: K,
    defaultValue?: (typeof combinedEnv)[K],
  ): (typeof combinedEnv)[K] => {
    return getEnv(combinedEnv, property, defaultValue);
  };

  const getValidatedEnvOrFail = <K extends keyof typeof combinedEnv>(
    property: K,
  ): NonNullable<(typeof combinedEnv)[K]> => {
    return getEnvOrFail(combinedEnv, property);
  };

  return {
    env: combinedEnv,
    getEnv: getValidatedEnv,
    getEnvOrFail: getValidatedEnvOrFail,
  };
}

export default createEnv;
